<template>
  <div v-loading="isLoading">
    <el-table
      ref="fuelsTable"
      :data="fuels"
      stripe
      @selection-change="handleSelectionChange"
    >
      <el-table-column
        v-if="canUpdate || canDelete"
        type="selection"
      />

      <el-table-column
        label="Name"
        prop="name"
        sortable
        min-width="320px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-' "
      />

      <el-table-column
        label="Code"
        prop="code"
        sortable
        width="120px"
        :formatter="(row, col, cellValue) => cellValue ? (cellValue || '-') : '-' "
      />

      <el-table-column
        label="Type"
        prop="type"
        sortable
        width="120px"
      >
        <template slot-scope="scope">
          <el-tag
            v-if="scope.row.type === 'buy'"
            type="info"
            size="small"
          >
            Buy
          </el-tag>

          <el-tag
            v-else-if="scope.row.type === 'sell'"
            type="primary"
            size="small"
          >
            Sell
          </el-tag>

          <el-tag
            v-else
            type="warning"
            size="small"
          >
            Unknown
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column
        label="Rate"
        prop="rate"
        sortable
        width="120px"
        :formatter="(row, col, cellValue) => cellValue ? (cellValue || '0') : '0' "
      />

      <el-table-column
        label="Started At"
        prop="date_start"
        sortable
        width="120px"
        :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        label="Ended At"
        prop="date_end"
        sortable
        width="120px"
        :formatter="(row, col, cellValue) => cellValue ? $moment(cellValue).format('DD/MM/YYYY') : '-'"
      />

      <el-table-column
        label="Carrier"
        prop="carrier"
        sortable
        :sort-method="sortCarrier"
        width="180px"
        show-overflow-tooltip
        :formatter="(row, col, cellValue) => cellValue ? (cellValue.name || '-') : '-'"
      />

      <el-table-column
        label="Status"
        sortable
        align="center"
        width="120px"
      >
        <template slot-scope="scope">
          <el-popover
            placement="top"
            :content="scope.row.profile ? `Updated By: ${scope.row.profile.name} at ${$moment(scope.row.updated_at).format('DD/MM/YYYY HH:mm')}` : '-'"
            trigger="hover"
          >
            <template slot="reference">
              <el-tag
                v-if="scope.row.active"
                type="success"
                size="small"
              >
                Active
              </el-tag>

              <el-tag
                v-else
                type="warning"
                size="small"
              >
                Inactive
              </el-tag>
            </template>
          </el-popover>
        </template>
      </el-table-column>

      <el-table-column
        label="Actions"
        align="right"
        width="270px"
      >
        <template slot-scope="scope">
          <el-button
            v-if="canUpdate"
            size="mini"
            @click.native="editFuel(scope.$index, scope.row)"
          >
            Edit
          </el-button>

          <el-button
            v-if="canDelete"
            size="mini"
            type="danger"
            @click.native="deleteFuel(scope.$index, scope.row)"
          >
            Delete
          </el-button>

          <el-dropdown
            :hide-on-click="false"
            style="margin-left: 10px;"
            trigger="click"
          >
            <el-button size="mini">
              <FontAwesomeIcon :icon="['far', 'ellipsis-h']" />
            </el-button>

            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item
                v-if="canUpdate"
                @click.native="linkedAccounts(scope.$index, scope.row)"
              >
                Accounts
              </el-dropdown-item>

              <el-dropdown-item
                v-if="canUpdate"
                @click.native="linkedServices(scope.$index, scope.row)"
              >
                Services
              </el-dropdown-item>

              <el-dropdown-item
                v-if="canCreate"
                @click.native="duplicateFuel(scope.$index, scope.row)"
              >
                Duplicate
              </el-dropdown-item>

              <el-dropdown-item @click.native="history(scope.$index, scope.row)">
                History
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>

    <!-- Pagination -->
    <el-pagination
      class="text-center pt-3"
      :current-page.sync="page"
      :page-sizes="[10, 20, 50, 100]"
      :page-size.sync="perPage"
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      @size-change="loadTable"
      @current-change="loadTable"
    />

    <!-- Visibility Form -->
    <TableColumnsVisibility
      v-if="isShowVisibilityDialog"
      ref="visibilityForm"
      v-model="visibility"
      :is-show.sync="isShowVisibilityDialog"
    />

    <!-- Edit Form -->
    <FuelLevyEditForm
      v-if="isShowEditForm"
      ref="editForm"
      :fuel="fuel"
      :is-show.sync="isShowEditForm"
      @saved="loadTable"
    />

    <!-- Linked Accounts -->
    <FuelLinkedAccountsForm
      v-if="isShowLinkedAccountsForm"
      ref="linkedAccountsForm"
      :is-show.sync="isShowLinkedAccountsForm"
      :fuel="fuel"
      :group="group"
    />

    <!-- Linked Services -->
    <FuelLinkedServicesForm
      v-if="isShowServicesForm"
      ref="linkedServicesForm"
      :is-show.sync="isShowServicesForm"
      :fuel="fuel"
    />

    <!-- Duplicate Form -->
    <FuelDuplicateForm
      v-if="isShowDuplicateForm"
      ref="duplicateForm"
      :is-show.sync="isShowDuplicateForm"
      :old-fuel="fuel"
    />

    <FuelBulkUpdateForm
      v-if="isShowBulkUpdateForm"
      ref="bulkUpdateForm"
      :is-show.sync="isShowBulkUpdateForm"
      :fuels="multiSelected"
    />
  </div>
</template>

<script>
import TableColumnsVisibility from '@/components/Application/Manage/TableColumnsVisibility'
import FuelLevyEditForm from '@/components/Application/Manage/FuelLevies/FuelLevyEditForm'
import FuelLinkedAccountsForm from '@/components/Application/Manage/FuelLevies/FuelLinkedAccountsForm'
import FuelLinkedServicesForm from '@/components/Application/Manage/FuelLevies/FuelLinkedServicesForm'
import FuelDuplicateForm from '@/components/Application/Manage/FuelLevies/FuelDuplicateForm'
import Filter, { IS } from '@/plugins/sdk/Filter'
import { CREATE, UPDATE, DELETE } from '@/plugins/user'
import FuelBulkUpdateForm from './FuelBulkUpdateForm.vue'

export default {
  components: {
    TableColumnsVisibility,
    FuelLevyEditForm,
    FuelLinkedAccountsForm,
    FuelLinkedServicesForm,
    FuelDuplicateForm,
    FuelBulkUpdateForm
  },
  props: {
    filters: {
      type: Array,
      required: true
    },
    sort: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      multiSelected: [],
      isShowBulkUpdateForm: false,
      actions: [
        {
          hidden: this.$user.cannot(CREATE, this.$sdk.fuel),
          name: 'Create',
          icon: ['fal', 'plus'],
          event: () => {
            this.fuel = new this.$sdk.fuel()
            this.isShowEditForm = true
          }
        },
        {
          name: 'Refresh',
          icon: ['fal', 'sync'],
          event: this.loadTable
        },
        {
          name: 'Bulk Update',
          icon: ['fal', 'edit'],
          event: () => {
            if (this.multiSelected.length === 0) {
              this.$message({ message: 'Please select at least one fuel', type: 'warning' })
              return
            }
            this.isShowBulkUpdateForm = true
          }
        }
      ],
      fuels: [],
      page: 1,
      perPage: 20,
      total: 0,
      isLoading: true,
      fuel: null,
      isShowVisibilityDialog: false,
      isShowEditForm: false,
      group: null,
      isShowLinkedAccountsForm: false,
      isShowServicesForm: false,
      isShowDuplicateForm: false,
      visibility: {
        group: false,
        id: false,
        name: true,
        code: true,
        type: true,
        rate: true,
        date_start: true,
        date_end: false,
        carrier: true,
        active: true,
        deleted_date: false
      }
    }
  },
  computed: {
    canCreate () {
      return this.$user.can(CREATE, this.$sdk.fuel)
    },
    canUpdate () {
      return this.$user.can(UPDATE, this.$sdk.fuel)
    },
    canDelete () {
      return this.$user.can(DELETE, this.$sdk.fuel)
    }
  },
  methods: {
    handleSelectionChange (selected) {
      this.multiSelected = selected
    },
    sortCarrier (a, b) {
      return a.carrier.name.localeCompare(b.carrier.name)
    },
    loadTable () {
      this.isLoading = true
      this.$sdk.fuel.list(builder => {
        builder.include = ['group', 'carrier', 'profile']
        builder.page = this.page
        builder.limit = this.perPage
        builder.order = `${this.sort.field}:${this.sort.direction}`
        builder.filters = this.filters
      })
        .then(res => {
          this.fuels = res
          this.total = res.total
        })
        .finally(() => { this.isLoading = false })
    },
    linkedAccounts (index, row) {
      this.fuel = row
      this.group = row.group
      this.isShowLinkedAccountsForm = true
    },
    linkedServices (index, row) {
      this.fuel = row
      this.isShowServicesForm = true
    },
    duplicateFuel (index, row) {
      this.fuel = row
      this.isShowDuplicateForm = true
    },
    history (index, row) {
      const existedGroupFilterIndex = this.filters.findIndex(f => f.field === 'group')
      if (existedGroupFilterIndex !== -1) {
        this.filters[existedGroupFilterIndex].value = row.group.id
      } else {
        this.filters.push(new Filter('group', IS, row.group.id))
      }
      this.loadTable()
    },
    editFuel (index, row) {
      this.fuel = row
      this.isShowEditForm = true
    },
    deleteFuel (index, row) {
      this.$confirm('Are you sure to delete this fuel?', 'Delete', {
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel',
        type: 'warning'
      }).then(() => {
        this.isLoading = true
        row.delete()
          .then(() => this.$message({ message: 'Successfully deleted', type: 'success' }))
          .finally(() => this.loadTable())
      }).catch(() => {})
    }
  }
}
</script>

<style>

</style>
